import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { supabase } from '../lib/supabase';
import api from '../lib/api';
import { usePricing } from '../hooks/usePricing';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Skeleton } from './ui/skeleton';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  Plus, Briefcase, Eye, Phone, Pencil, Pause, Play, Archive, AlertTriangle, Loader2,
} from 'lucide-react';

const STATUS_BADGE = {
  draft: { labelKey: 'services.statusDraft', className: 'bg-gray-100 text-gray-600 border-gray-200' },
  active: { labelKey: 'services.statusActive', className: 'bg-green-50 text-green-700 border-green-200' },
  paused: { labelKey: 'services.statusPaused', className: 'bg-amber-50 text-amber-700 border-amber-200' },
  archived: { labelKey: 'services.statusArchived', className: 'bg-red-50 text-red-700 border-red-200' },
};

/**
 * Dashboard "My Services" section (mirrors MyBannersSection). Lists the caller's own listings from
 * GET /api/pro-listings/mine with status badge, thumbnail, and AGGREGATE stats (view_count +
 * contact_click_count — never client identities). Status actions POST /status.
 *
 * Self-hides when pricing.services_enabled is false (renders null).
 */
const MyServiceListingsSection = ({ profile }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pricing } = usePricing();
  const servicesEnabled = Boolean(pricing?.services_enabled);

  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [busyAction, setBusyAction] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null);

  const fetchMine = useCallback(async () => {
    if (!profile?.id) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) { setLoading(false); return; }
      const res = await api.get('/api/pro-listings/mine', {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      setListings(res.data?.listings || []);
    } catch (error) {
      console.error('[MyServiceListingsSection] fetch failed:', error);
    } finally {
      setLoading(false);
    }
  }, [profile?.id]);

  useEffect(() => {
    // Flag-off (incl. while pricing resolves): leave loading untouched — the flag-off render
    // path returns null; flipping loading here caused a false empty-state flash on flag flip.
    if (servicesEnabled) {
      fetchMine();
    }
  }, [servicesEnabled, fetchMine]);

  const runStatusAction = async (listing, action) => {
    if (busyId) return;
    setBusyId(listing.id);
    setBusyAction(action);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        toast.error(t('services.loginRequired'));
        return;
      }
      await api.post(`/api/pro-listings/${listing.id}/status`, { action }, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const successKey = {
        pause: 'services.pauseSuccess',
        reactivate: 'services.reactivateSuccess',
        archive: 'services.archiveSuccess',
      }[action];
      toast.success(t(successKey));
      await fetchMine();
    } catch (err) {
      const detail = typeof err?.response?.data?.detail === 'string' ? err.response.data.detail : '';
      toast.error(detail || t('services.statusActionError'));
    } finally {
      setBusyId(null);
      setBusyAction(null);
      setArchiveTarget(null);
    }
  };

  // Flag off → render nothing (spec §12.1 / brief).
  if (!servicesEnabled) return null;

  if (loading) {
    return (
      <div>
        <h2 className="font-heading text-2xl font-semibold mb-4">{t('services.myListingsTitle')}</h2>
        <Card><Skeleton className="h-32" /></Card>
      </div>
    );
  }

  return (
    <>
      <div data-testid="my-service-listings-section">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-heading text-2xl font-semibold">
            {t('services.myListingsTitle')}{listings.length > 0 ? ` (${listings.length})` : ''}
          </h2>
          <Button size="sm" variant="outline" onClick={() => navigate('/services/listings/create')}>
            <Plus size={16} className="mr-1" aria-hidden="true" />
            {t('services.listAService')}
          </Button>
        </div>

        {listings.length === 0 ? (
          <Card className="border-dashed p-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <Briefcase className="text-muted-foreground shrink-0" size={18} aria-hidden="true" />
              <p className="text-sm text-muted-foreground line-clamp-2">{t('services.myListingsEmpty')}</p>
            </div>
            <Button size="sm" className="shrink-0" onClick={() => navigate('/services/listings/create')}>
              <Plus size={16} className="mr-1" aria-hidden="true" />
              {t('services.listAService')}
            </Button>
          </Card>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {listings.map((l) => {
              const badge = STATUS_BADGE[l.status] || STATUS_BADGE.draft;
              const primaryPhoto = Array.isArray(l.photos) && l.photos[0]?.url ? l.photos[0].url : null;
              const isBusy = busyId === l.id;
              return (
                <Card key={l.id} className="overflow-hidden">
                  <div className="aspect-[3/2] bg-muted flex items-center justify-center">
                    {primaryPhoto ? (
                      <img src={primaryPhoto} alt={l.title} className="w-full h-full object-cover" />
                    ) : (
                      <Briefcase className="text-muted-foreground" size={24} aria-hidden="true" />
                    )}
                  </div>
                  <div className="p-3 space-y-2">
                    <p className="text-sm font-medium truncate" title={l.title}>{l.title}</p>
                    <div className="flex items-center justify-between flex-wrap gap-1">
                      <Badge variant="outline" className={badge.className}>{t(badge.labelKey)}</Badge>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1" title={t('services.views')}>
                          <Eye size={12} aria-hidden="true" />
                          <span className="sr-only">{t('services.views')}: </span>{l.view_count || 0}
                        </span>
                        <span className="flex items-center gap-1" title={t('services.contactReveals')}>
                          <Phone size={12} aria-hidden="true" />
                          <span className="sr-only">{t('services.contactReveals')}: </span>{l.contact_click_count || 0}
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 pt-1 border-t border-dashed">
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 min-w-[70px] text-xs"
                        disabled={!!busyId}
                        onClick={() => navigate(`/services/listings/${l.id}/edit`)}
                      >
                        <Pencil size={13} className="mr-1" aria-hidden="true" />
                        {t('services.edit')}
                      </Button>

                      {l.status === 'active' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 min-w-[70px] text-xs"
                          disabled={!!busyId}
                          onClick={() => runStatusAction(l, 'pause')}
                        >
                          {isBusy && busyAction === 'pause'
                            ? <Loader2 size={13} className="mr-1 animate-spin" aria-hidden="true" />
                            : <Pause size={13} className="mr-1" aria-hidden="true" />}
                          {t('services.pause')}
                        </Button>
                      )}

                      {(l.status === 'paused' || l.status === 'draft' || l.status === 'archived') && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 min-w-[70px] text-xs"
                          disabled={!!busyId}
                          onClick={() => runStatusAction(l, 'reactivate')}
                        >
                          {isBusy && busyAction === 'reactivate'
                            ? <Loader2 size={13} className="mr-1 animate-spin" aria-hidden="true" />
                            : <Play size={13} className="mr-1" aria-hidden="true" />}
                          {t('services.reactivate')}
                        </Button>
                      )}

                      {l.status !== 'archived' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 min-w-[70px] text-xs text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                          disabled={!!busyId}
                          onClick={() => setArchiveTarget(l)}
                        >
                          <Archive size={13} className="mr-1" aria-hidden="true" />
                          {t('services.archive')}
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={!!archiveTarget} onOpenChange={(open) => { if (!open && !busyId) setArchiveTarget(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Archive size={20} aria-hidden="true" />
              {t('services.archiveConfirmTitle')}
            </DialogTitle>
            <DialogDescription>{t('services.archiveConfirmBody')}</DialogDescription>
          </DialogHeader>
          <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            <AlertTriangle size={16} className="mt-0.5 shrink-0 text-red-600" aria-hidden="true" />
            <span>{t('services.archiveConfirmWarning')}</span>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" disabled={!!busyId} onClick={() => setArchiveTarget(null)}>
              {t('services.keepListing')}
            </Button>
            <Button
              variant="destructive"
              disabled={!!busyId}
              onClick={() => archiveTarget && runStatusAction(archiveTarget, 'archive')}
            >
              {busyId && <Loader2 size={16} className="mr-2 animate-spin" aria-hidden="true" />}
              {t('services.archiveConfirmAction')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MyServiceListingsSection;
